import { Alert, Button, Card, Form, Input, Radio, Select, Space, Typography, message } from "antd";
import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import { createWorkspace } from "services/workspaceService";
import olreadiaService from "services/olreadiaService";
import { organizationName } from "./workspaceUtils";
import { OLIVEEB_COLORS, workspaceButtonStyles } from "./colors";

const { Text } = Typography;

const trimValue = (value) => String(value || "").trim();
const pageContent = (data) => (Array.isArray(data) ? data : data?.content || []);

const WorkspaceCreatePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshWorkspaces, selectWorkspace } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const [form] = Form.useForm();
  const [organizationForm] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [creatingOrganization, setCreatingOrganization] = useState(false);
  const [organizationPanelOpen, setOrganizationPanelOpen] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [workspaceType, setWorkspaceType] = useState("PERSONAL");
  const selectedOrganizationId = Form.useWatch("organizationId", form) || "";
  const organizationWorkspace = workspaceType === "ORGANIZATION";

  const defaultName = useMemo(() => {
    const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
    return name ? `${name}'s workspace` : "My Care workspace";
  }, [user?.firstName, user?.lastName]);

  const loadOrganizationOptions = useCallback(async () => {
    if (!actorId) return;

    try {
      setLoadingOrganizations(true);
      const [entityPage, typePage] = await Promise.all([
        olreadiaService.organizations.listForUser(actorId, {
          page: 0,
          size: 50,
        }),
        olreadiaService.businessLocationTypes.list({ active: true }),
      ]);
      setOrganizations(pageContent(entityPage));
      setLocationTypes(pageContent(typePage));
    } catch (error) {
      message.error(error?.message || "Failed to load organizations.");
    } finally {
      setLoadingOrganizations(false);
    }
  }, [actorId]);

  useEffect(() => {
    if (organizationWorkspace) {
      loadOrganizationOptions();
    }
  }, [loadOrganizationOptions, organizationWorkspace]);

  useEffect(() => {
    if (!organizationWorkspace) {
      form.setFieldsValue({ organizationId: undefined });
      setOrganizationPanelOpen(false);
    }
  }, [form, organizationWorkspace]);

  const organizationOptions = useMemo(
    () =>
      organizations
        .filter((entity) => entity?.id)
        .map((entity) => ({
          value: entity.id,
          label: organizationName(entity, entity.id),
        })),
    [organizations],
  );

  const locationTypeOptions = useMemo(
    () =>
      locationTypes
        .filter((type) => type?.id)
        .map((type) => ({
          value: type.id,
          label: type.label || type.code || type.id,
        })),
    [locationTypes],
  );

  const createOrganization = async (values) => {
    try {
      setCreatingOrganization(true);
      const entity = await olreadiaService.organizations.create(
        {
          name: trimValue(values.name),
          description: trimValue(values.description),
          ein: trimValue(values.ein),
          businessLocationTypeId: values.businessLocationTypeId,
          status: "DRAFT",
          contactInformation: {
            primaryEmail: trimValue(values.primaryEmail),
            primaryPhone: trimValue(values.primaryPhone),
            websiteUrl: trimValue(values.websiteUrl),
            contactPersonName: trimValue(values.contactPersonName),
          },
        },
        actorId,
      );

      if (!entity?.id) {
        throw new Error("Created organization id is missing.");
      }

      setOrganizations((current) => [entity, ...current]);
      setWorkspaceType("ORGANIZATION");
      form.setFieldsValue({
        accountType: "ORGANIZATION",
        organizationId: entity.id,
      });
      organizationForm.resetFields();
      setOrganizationPanelOpen(false);
      message.success("Organization created and selected.");
    } catch (error) {
      message.error(error?.message || "Failed to create organization.");
    } finally {
      setCreatingOrganization(false);
    }
  };

  const handleCreate = async (values) => {
    try {
      setSaving(true);
      const organizationId = trimValue(values.organizationId);
      const workspace = await createWorkspace({
        actorId,
        name: trimValue(values.name),
        description: trimValue(values.description),
        accountType: workspaceType,
        organizationId:
          workspaceType === "ORGANIZATION" && organizationId
            ? organizationId
            : undefined,
      });

      await refreshWorkspaces();
      if (workspace?.id) {
        selectWorkspace(workspace.id);
      }

      message.success("Workspace created.");
      navigate(workspace?.id ? paths.workspaceDetails(workspace.id) : paths.workspaces);
    } catch (error) {
      message.error(error?.message || "Failed to create workspace.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon={<AppstoreOutlined />}
          title="Add Workspace"
          subtitle="Create a workspace for projects, testing, subscriptions, charges, and collaboration"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Button
          icon={<ArrowLeftOutlined />}
          style={workspaceButtonStyles.back}
          onClick={() => navigate(paths.workspaces)}
        >
          Back
        </Button>
      </div>

      <Card
        style={{
          position: "relative",
          overflow: "hidden",
          maxWidth: 860,
          borderRadius: 8,
          borderColor: OLIVEEB_COLORS.oliveebBlueSoft,
          background: `linear-gradient(135deg, #ffffff 0%, ${OLIVEEB_COLORS.bgSoft} 48%, ${OLIVEEB_COLORS.oliveebBlueSoft} 100%)`,
          boxShadow: "0 18px 44px rgba(0, 64, 56, 0.10)",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${OLIVEEB_COLORS.oliveebGreen}, ${OLIVEEB_COLORS.oliveebBlue}, ${OLIVEEB_COLORS.oliveebOrange})`,
          }}
        />

        <Space direction="vertical" size={18} style={{ width: "100%" }}>
          <div>
            <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkBlue }}>
              Start with a clean workspace context
            </Text>
            <Text style={{ display: "block", color: OLIVEEB_COLORS.textMuted, marginTop: 4 }}>
              The new workspace becomes available in the top-right workspace picker after it is created.
            </Text>
          </div>

          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            initialValues={{
              name: defaultName,
              accountType: "PERSONAL",
            }}
            onFinish={handleCreate}
          >
            <Form.Item
              name="name"
              label="Workspace name"
              rules={[
                { required: true, message: "Enter a workspace name." },
                {
                  validator: (_, value) =>
                    trimValue(value)
                      ? Promise.resolve()
                      : Promise.reject(new Error("Enter a workspace name.")),
                },
              ]}
            >
              <Input
                size="large"
                placeholder="Workspace name"
                prefix={<AppstoreOutlined style={{ color: OLIVEEB_COLORS.oliveebBlue }} />}
                style={{ borderRadius: 999 }}
              />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <Input.TextArea
                rows={3}
                placeholder="Optional description"
                style={{ borderRadius: 18 }}
              />
            </Form.Item>

            <Form.Item name="accountType" label="Workspace type">
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                value={workspaceType}
                onChange={(event) => {
                  const nextType = event.target.value;
                  setWorkspaceType(nextType);
                  form.setFieldsValue({ accountType: nextType });
                  if (nextType === "PERSONAL") {
                    form.setFieldsValue({ organizationId: undefined });
                    setOrganizationPanelOpen(false);
                  }
                }}
                options={[
                  { label: "Personal", value: "PERSONAL" },
                  { label: "Organization", value: "ORGANIZATION" },
                ]}
              />
            </Form.Item>

            {organizationWorkspace ? (
              <Form.Item
                name="organizationId"
                hidden
                rules={[
                  {
                    validator: (_, value) => {
                      if (value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Select an organization for organization workspaces."),
                      );
                    },
                  },
                ]}
              >
                <Input />
              </Form.Item>
            ) : null}

            {organizationWorkspace ? (
              <div
                style={{
                  border: `1px solid ${OLIVEEB_COLORS.border}`,
                  borderRadius: 8,
                  padding: 14,
                  marginBottom: 18,
                  background: OLIVEEB_COLORS.card,
                }}
              >
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                    <div>
                      <Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
                        Select Organization
                      </Text>
                      <Text style={{ display: "block", color: OLIVEEB_COLORS.textMuted, fontSize: 12 }}>
                        Pick from organizations linked to your user, or create one now.
                      </Text>
                    </div>
                    <Space wrap>
                      <Button
                        icon={<ReloadOutlined />}
                        loading={loadingOrganizations}
                        onClick={loadOrganizationOptions}
                      >
                        Refresh
                      </Button>
                      <Button
                        icon={<PlusOutlined />}
                        style={workspaceButtonStyles.organization}
                        onClick={() => setOrganizationPanelOpen((open) => !open)}
                      >
                        Create Organization
                      </Button>
                    </Space>
                  </div>

                  <div>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>
                      Organization
                    </Text>
                    {organizationOptions.length ? (
                      <Select
                        allowClear
                        showSearch
                        loading={loadingOrganizations}
                        placeholder="Select one of your organizations"
                        value={selectedOrganizationId || undefined}
                        options={organizationOptions}
                        optionFilterProp="label"
                        style={{ width: "100%" }}
                        onChange={(organizationId) => {
                          form.setFieldsValue({
                            organizationId: organizationId || undefined,
                          });
                        }}
                      />
                    ) : (
                      <Alert
                        type="info"
                        showIcon
                        message="No linked organizations found for your user."
                      />
                    )}
                    <Text type="secondary" style={{ display: "block", marginTop: 6, fontSize: 12 }}>
                      Organization workspaces must be linked to a selected organization.
                    </Text>
                  </div>
                </Space>
              </div>
            ) : null}

            {organizationWorkspace && organizationPanelOpen ? (
              <div
                style={{
                  border: `1px solid ${OLIVEEB_COLORS.oliveebBlueSoft}`,
                  borderRadius: 8,
                  padding: 14,
                  marginBottom: 18,
                  background: OLIVEEB_COLORS.oliveebBlueSoft,
                }}
              >
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkBlue }}>
                    New Organization
                  </Text>
                  <Form
                    form={organizationForm}
                    layout="vertical"
                    requiredMark={false}
                    component={false}
                    onFinish={createOrganization}
                  >
                    <Form.Item
                      name="name"
                      label="Organization name"
                      rules={[{ required: true, message: "Enter organization name." }]}
                    >
                      <Input prefix={<ShopOutlined />} placeholder="Organization name" />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                      <Input.TextArea rows={2} placeholder="Optional description" />
                    </Form.Item>
                    <Form.Item
                      name="businessLocationTypeId"
                      label="Organization location type"
                      rules={[{ required: true, message: "Select an organization location type." }]}
                    >
                      <Select
                        showSearch
                        loading={loadingOrganizations}
                        placeholder="Select organization location type"
                        optionFilterProp="label"
                        options={locationTypeOptions}
                      />
                    </Form.Item>
                    <Form.Item name="ein" label="EIN">
                      <Input placeholder="Optional EIN" />
                    </Form.Item>
                    <Form.Item name="primaryEmail" label="Primary email">
                      <Input placeholder="organization@example.com" />
                    </Form.Item>
                    <Form.Item name="primaryPhone" label="Primary phone">
                      <Input placeholder="+1..." />
                    </Form.Item>
                    <Form.Item name="websiteUrl" label="Website">
                      <Input placeholder="https://..." />
                    </Form.Item>
                    <Form.Item name="contactPersonName" label="Contact person">
                      <Input placeholder="Contact name" />
                    </Form.Item>
                    <Space wrap>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        loading={creatingOrganization}
                        onClick={async () => {
                          try {
                            const values = await organizationForm.validateFields();
                            await createOrganization(values);
                          } catch (error) {
                            if (!error?.errorFields) {
                              message.error(error?.message || "Failed to create organization.");
                            }
                          }
                        }}
                        style={{
                          borderColor: OLIVEEB_COLORS.oliveebGreen,
                          background: OLIVEEB_COLORS.oliveebGreen,
                          fontWeight: 700,
                        }}
                      >
                        Create and Select
                      </Button>
                      <Button onClick={() => setOrganizationPanelOpen(false)}>
                        Cancel
                      </Button>
                    </Space>
                  </Form>
                </Space>
              </div>
            ) : null}

            <Space wrap>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                icon={<CheckCircleOutlined />}
                loading={saving}
                style={{
                  borderRadius: 999,
                  borderColor: OLIVEEB_COLORS.oliveebGreen,
                  background: OLIVEEB_COLORS.oliveebGreen,
                  fontWeight: 800,
                }}
              >
                Create Workspace
              </Button>
              <Button
                size="large"
                disabled={saving}
                onClick={() => navigate(paths.workspaces)}
                style={{ borderRadius: 999 }}
              >
                Cancel
              </Button>
            </Space>
          </Form>
        </Space>
      </Card>
    </div>
  );
};

export default WorkspaceCreatePage;
